howspace.components.userPicker = (() => {
    'use strict';

    const searchUsers = e => {
        const userSearchInput = $(e.currentTarget);
        const dropdown = userSearchInput.closest('.dropdown');
        const userPicker = dropdown.closest('.howspace-user-picker');
        const search = e.detail.value;

        if (search === '' && userSearchInput[0].dataset.allowEmpty !== 'true') {
            howspace.components.dropdown.close(dropdown);
            return;
        }

        const excludeUserIds = [];
        userPicker.find('.users-list .user').each(function () {
            excludeUserIds.push($(this).data('user-id'));
        });

        const dataPayload = {
            search: search,
            exclude: excludeUserIds,
            email_recipients: userPicker.data('email-recipients') || false,
            only_admins: userPicker.data('only-admins') || false,
        };

        real.ajax.json(real.baseUri + userPicker.data('search-url'), {
            type: 'POST',
            data: JSON.stringify(dataPayload),
            success: response => {
                if (response.search !== undefined && response.search !== userSearchInput.val().trim()) {
                    // discard the results since the search input value has changed since the request was sent
                    return;
                }
                let html = response.html;
                if (response.html.trim() === '') {
                    html = '<div class="p-3">' + userPicker.data('no-users-found-text') + '</div>';
                }
                const menu = userPicker.find('.howspace-menu');
                menu.removeClass('d-none');
                const menuItems = menu.find('.menu-items');
                menuItems.html(html);
                // Endpoints that answer in pages send back where the next one starts. The ones that do not leave
                // this null and so never get a control, which is every other picker sharing this component.
                menuItems.data('paging-payload', dataPayload);
                addLoadMoreControl(menuItems, response.next_offset ?? null);
                howspace.components.init(userPicker);
                howspace.components.dropdown.open(dropdown, false);
                howspace.dispatchEvent(userPicker, howspace.components.userPicker.USERS_SEARCHED);
            }
        });
    };

    /**
     * Marks the control that asks for the next page. Deliberately not the `user` class the rows carry: the arrow
     * walk in simple-text-editor.js advances on that class, and Enter on it would reach addUser, which inserts no
     * mention but still clears the search and closes the picker. Not the People widget's `load-more-users-btn`
     * either, which that widget binds by class within itself, and it renders a picker of its own in edit mode.
     */
    const LOAD_MORE_CLASS = 'user-picker-more-btn';

    /**
     * Puts the control at the end of the list when there is a page after this one, carrying the offset that page
     * starts at. Cloned from a template rather than left in the markup, because searchUsers replaces the whole of
     * .menu-items on every search.
     */
    const addLoadMoreControl = (menuItems, nextOffset) => {
        if (nextOffset === null) {
            return;
        }

        const template = menuItems.closest('.howspace-user-picker').find('.load-more-template').html();
        if (!template) {
            return;
        }

        menuItems.append($(template).addClass(LOAD_MORE_CLASS).attr('data-next-offset', nextOffset));
    };

    const loadMoreUsers = e => {
        // dropdown.js closes the menu on any click that reaches document.body from a menu item, and says so: the
        // sanctioned way to keep a menu open is to stop the event in the item's own handler. This control asks for
        // more of the list rather than picking from it, so the picker has to stay where it is.
        e.stopPropagation();
        e.preventDefault();

        const control = $(e.currentTarget);
        const menuItems = control.closest('.menu-items');
        const userPicker = control.closest('.howspace-user-picker');
        const payload = menuItems.data('paging-payload');
        const offset = Number(control.attr('data-next-offset'));

        if (!payload || control.prop('disabled')) {
            return;
        }

        control.prop('disabled', true);
        real.button.loading(control);

        real.ajax.json(real.baseUri + userPicker.data('search-url'), {
            type: 'POST',
            data: JSON.stringify({ ...payload, offset: offset }),
            success: response => {
                // A search that moved on replaced the list, and with it this control, so a response arriving late
                // belongs to a list that is no longer on screen.
                if (!$.contains(document, control[0]) || menuItems.data('paging-payload') !== payload) {
                    return;
                }

                const html = response.html ?? '';
                const wasActive = control.hasClass('active');

                control.remove();
                // Held before the append so the rows that arrive can be told from the ones already listed.
                // Reparsing the markup instead would hand back a detached copy of them, and marking that copy
                // leaves the list on screen with nothing highlighted.
                const lastExisting = menuItems.children().last();
                menuItems.append(html);
                addLoadMoreControl(menuItems, response.next_offset ?? null);
                howspace.components.init(userPicker);

                if (!wasActive) {
                    return;
                }

                // The highlight stays on the control while there are pages left. On the last page there is no
                // control to hold it, so it goes to the first row that just arrived rather than back to the top.
                const nextControl = menuItems.find('.' + LOAD_MORE_CLASS);
                if (nextControl.length > 0) {
                    nextControl.addClass('active');
                    return;
                }

                const arrived = lastExisting.length > 0
                    ? lastExisting.nextAll('.user').first()
                    : menuItems.find('.user').first();

                // Class-based selection, not DOM focus. The mention picker keeps focus in the editor so typing
                // continues, so moving it here would break that flow. A picker that pages and is driven by real
                // focus would need to move focus as well: removing the control drops it, and dropdown.js then
                // reads activeElement as outside the menu and sends the next arrow key to the first item.
                arrived.addClass('active');
            },
            error: () => {
                real.button.reset(control);
                control.prop('disabled', false);
            }
        });
    };

    const handleFocus = e => {
        const userPicker = $(e.target).closest('.howspace-user-picker');
        const dropdown = userPicker.find('.dropdown');
        const userSearchInput = dropdown.find('.howspace-form-search-input');

        if (userSearchInput.val().trim() !== '') {
            howspace.components.dropdown.open(dropdown, false);
        }
    };

    const addUser = e => {
        let user = $(e.target);

        // The control is a menuitem inside role="menu" like every row, so the delegated click above catches it too.
        if (user.closest('.' + LOAD_MORE_CLASS).length > 0) {
            return;
        }
        if (!user.hasClass('.user')) {
            user = user.closest('.user');
        }
        const dropdown = user.closest('.dropdown');
        const userPicker = user.closest('.howspace-user-picker');

        if (userPicker.find('.users-list .user[data-user-id="' + user.data('user-id') + '"]').length > 0) {
            return;
        }

        // Clear the search input value.
        howspace.components.searchInput.clearSearch(dropdown.find('.howspace-form-search-input'));

        // Create a clone of the user menu item and turn it into a user in the user list.
        const userClone = $('<div class="user flex gap-2"></div>');
        userClone.dataAndAttr('user-id', user.data('user-id'));
        userClone.html(user.html());

        // Append the remove user button and hidden input.
        let template = userPicker.find('.remove-user-btn-template').html();
        template = template.replace('${id}', user.data('user-id'));
        userClone.append(template);

        // Append the user to the user list.
        userPicker.find('.users-list').append(userClone);

        if (user.data('user-id')) {
            howspace.dispatchEvent(userPicker, howspace.components.userPicker.USER_ADDED, {
                user: {
                    id: user.data('user-id'),
                    displayName: user.find('.user-name').last().text().trim(),
                }
            });
        } else if (user.data('userlist-id')) {
            howspace.dispatchEvent(userPicker, howspace.components.userPicker.USERLIST_ADDED, {
                userlist: {
                    id: user.data('userlist-id'),
                    displayName: user.find('.user-name').last().text().trim(),
                }
            });
        } else if (user.data('team-id')) {
            howspace.dispatchEvent(userPicker, howspace.components.userPicker.TEAM_ADDED, {
                team: {
                    id: user.data('team-id'),
                    displayName: user.find('.user-name').last().text().trim(),
                }
            });
        }

        if (disableAnimations) {
            return;
        }

        userClone.velocity('slideDown', {
            duration: howspace.getAnimationDuration(userPicker, '--user-animation-duration'),
            display: 'flex',
            complete: () => {
                userClone.removeAttr('style');
            }
        });

        requestAnimationFrame(() => {
            if (dropdown.is('.expanded')) {
                // Close the dropdown.
                howspace.components.dropdown.close(dropdown);
            }
        });
    }

    const removeUser = e => {
        const btn = e.target;
        const user = btn.closest('.user');
        const userPicker = user.closest('.howspace-user-picker');
        const animationDuration = howspace.getAnimationDuration(userPicker, '--user-animation-duration');
        $.Velocity(user, 'slideUp', {
            duration: animationDuration,
            complete: () => {
                const userId = user.dataset.userId;
                user.parentNode.removeChild(user);
                howspace.dispatchEvent(userPicker, howspace.components.userPicker.USER_REMOVED, {
                    user: {
                        id: userId
                    }
                });
            }
        });
    };

    // Temporary bulk mode pending for a backend that returns HTML we can directly
    // insert into the DOM.
    let disableAnimations = false;
    function setBulkMode(value = true) {
        if (value) {
            disableAnimations = true;
            window.FontAwesome.dom.unwatch();
        } else {
            disableAnimations = false;
            window.FontAwesome.dom.watch();
        }
    }

    const handlePaste = e => {
        e.preventDefault();
        e.stopImmediatePropagation();

        if (!e.originalEvent?.isTrusted) {
            return;
        }

        const searchInput = $(e.target);
        const userPicker = searchInput.closest('.howspace-user-picker');

        real.button.loading(searchInput, userPicker);

        const dataPayload = {
            rawdata: e.originalEvent.clipboardData?.getData('text/plain') || '',
            email_recipients: userPicker.data('email-recipients') || false,
        };

        real.ajax.html(real.baseUri + '/users/search-by-email', {
            type: 'POST',
            data: JSON.stringify(dataPayload),
            success: html => {
                if (html.length > 20) {
                    setBulkMode();
                }
                html.forEach(user => {
                    $(user).addClass('d-none')
                        .appendTo(userPicker)
                        .one('click', addUser)
                        .trigger('click')
                        .remove();
                });
            },
            complete: () => {
                real.button.reset(searchInput, userPicker);
                setBulkMode(false);

                howspace.components.searchInput.clearSearch(searchInput);
                const dropdown = userPicker.find('.dropdown');
                if (dropdown.is('.expanded')) {
                    howspace.components.dropdown.close(dropdown);
                }
            }
        });
    }

    return {
        USER_ADDED: 'user-picker-user-added',
        USERLIST_ADDED: 'user-picker-userlist-added',
        TEAM_ADDED: 'user-picker-team-added',
        USER_REMOVED: 'user-picker-user-removed',
        USERS_SEARCHED: 'user-picker-users-searched',
        init: elem => {
            elem.findIncludingSelf('.howspace-user-picker:not(.howspace-user-picker-initialized)').each(function () {
                const userPicker = $(this);
                userPicker.addClass('howspace-user-picker-initialized');

                // Force rendering of icons in the template
                window.FontAwesome.dom.i2svg({ node: userPicker.find('.remove-user-btn-template').prop('content') });

                const dropdown = userPicker.find('.dropdown');
                dropdown.on('click', '[role="menuitem"]', addUser);

                const userSearchInput = dropdown.find('.howspace-form-search-input');
                userSearchInput.on('focus', handleFocus);
                userSearchInput.on('paste', handlePaste);
                userSearchInput.on(howspace.components.searchInput.SEARCH, searchUsers);

                userPicker.on('click', '.remove-user-btn', removeUser);

                dropdown.on('click', '.' + LOAD_MORE_CLASS, loadMoreUsers);
            });
        },
        destroy: elem => {
            elem.findIncludingSelf('.howspace-user-picker.howspace-user-picker-initialized').each(function () {
                const userPicker = $(this);
                userPicker.removeClass('howspace-user-picker-initialized');

                const dropdown = userPicker.find('.dropdown');
                dropdown.off('click', '[role="menuitem"]', addUser);

                const userSearchInput = dropdown.find('.howspace-form-search-input');
                userSearchInput.off('focus', handleFocus);
                userSearchInput.off('paste', handlePaste);
                userSearchInput.off(howspace.components.searchInput.SEARCH, searchUsers);

                userPicker.off('click', '.remove-user-btn', removeUser);

                dropdown.off('click', '.' + LOAD_MORE_CLASS, loadMoreUsers);
                dropdown.find('.menu-items').removeData('paging-payload');
            });
        },
        getSelectedUsers: userPicker => {
            const userIds = [];
            userPicker.find('.users-list .user').each(function () {
                userIds.push($(this).data('user-id'));
            });
            return userIds;
        }
    };
})();
